"use client";

import { useEffect, useMemo, useState, useRef, type MouseEvent } from "react";
import {
  IoCheckmark,
  IoGridOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import { LuList } from "react-icons/lu";
import { MdOutlineContentCopy, MdOutlineRemoveRedEye } from "react-icons/md";
import { FiEdit3 } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import LibraryHeaderFilters, {
  ALL_GAME_TYPE_FILTER,
  ALL_GROUP_FILTER,
  ALL_QUESTION_MODE_FILTER,
  ALL_SUBJECT_FILTER,
  QUESTION_MODE_SERIES_FILTER,
  QUESTION_MODE_SINGLE_FILTER,
  type FilterState,
} from "./libraryHeaderFilters";
import LibrarySortableHeader, {
  DEFAULT_LIBRARY_SORT_STATE,
  sortLibraryRows,
  type LibrarySortState,
} from "./LibrarySortableHeader";
import mainAPI from "../../services/main/mainAPI";
import gameWizardAPI from "../../services/gameWizard/gameWizardAPI";
import { formatCompactDate, formatDateShort } from "../../utils/formatDatetime";
import {
  GAME_TYPE_API_KEYS,
  getGameTypeLabel as getLocalizedGameTypeLabel,
  getLibraryGameTypeDisplay,
} from "../../utils/gameTypeLabels";
import ConfirmPopUp from "../popup/confirmPopUp";
import StatusPopUp from "../popup/statusPopUp";
import CopyPopUp from "../popup/copyPopUp";
import { GamePreviewModal } from "../games/shared/GamePreviewModal";
import "react-photo-view/dist/react-photo-view.css";
import { PhotoProvider, PhotoView } from "react-photo-view";
import sequenceAPI from "../../services/sequence/sequenceAPI";
import { useTranslations } from "next-intl";
import { CgNotes } from "react-icons/cg";
import TablePagination, {
  type PageSizeOption,
} from "../shared/TablePagination";
import { BsThreeDotsVertical } from "react-icons/bs";
import NotePopUp from "../note";
import InformationDetail, {
  type InformationDetailData,
  type InformationDetailNote,
  type InformationDetailUpdates,
} from "../informationDetail";
import GameEditorWizard, {
  type GameEditorWizardData,
} from "./gameEditorWizard";
import { MdOutlineControlPointDuplicate } from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import { FaBan } from "react-icons/fa";

const USER_STORAGE_CHANGED_EVENT = "user-storage-changed";

const defaultFilterState: FilterState = {
  search: "",
  subject: ALL_SUBJECT_FILTER,
  groupTopic: ALL_GROUP_FILTER,
  gameType: ALL_GAME_TYPE_FILTER,
  questionMode: ALL_QUESTION_MODE_FILTER,
  playMode: "ทั้งหมด",
};

type LibraryRow = {
  uniqueId: string;
  gameId: number | null;
  uuid: string;
  title: string;
  exerciseName: string;
  subject: string;
  topic: string;
  questionType: string;
  rawQuestionType: string | null;
  gameTypeKey: string;
  source: string;
  gameType: string;
  createdAt: string;
  createdAtRaw: string;
  classLevel: string;
  updatedAtRaw: string;
  createdBy: string;
  description: string;
  notes: InformationDetailNote[];
  thumbnail: string;
  thumbnailUrl: string;
  groupId: number | null;
  status: boolean;
  deletedAt: string | null;
  banAt: string | null;
  isBanned: boolean;
  hasThumb?: boolean;
  gameDefault?: boolean;
  sequenceDefault?: boolean;
};

type NoteEditorTarget = {
  row: LibraryRow;
  mode: "create" | "update";
  note?: InformationDetailNote;
};

type CopyTarget = {
  row: LibraryRow;
  generatedUuid: string;
};

type PilaThailandLibraryProps = {
  mode: "template" | "my-library";
  onOpenRecord?: (record: {
    uuid: string;
    gameType: string;
    source: string;
    gameId: number | null;
    title: string;
  }) => void;
};

type EditRecordTarget = {
  uuid: string;
  gameType: string;
  source: string;
  gameId: number | null;
  title: string;
};

type StatusToggleDialogContent = {
  confirmTitle: string;
  confirmMessage: string;
  successTitle: string;
  successMessage: string;
};

function getMyLibraryPublishButtonClass(
  isBanned: boolean,
  published: boolean,
): string {
  const base =
    "cursor-pointer inline-flex h-9 w-[148px] min-w-[148px] items-center justify-center gap-2 rounded-lg px-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-75 whitespace-nowrap";

  if (isBanned) {
    return `${base} bg-red-500`;
  }

  if (published) {
    return `${base} bg-[#E6F8EF]`;
  }

  return `${base} bg-[#E0E0E0]`;
}

const LIBRARY_LIST_GRID_WITH_STATUS =
  "grid-cols-[64px_1.1fr_0.5fr_1fr_0.9fr_0.9fr_1fr_minmax(188px,1.25fr)_0.8fr_76px_228px]";
const LIBRARY_LIST_GRID_WITHOUT_STATUS =
  "grid-cols-[64px_1.1fr_0.5fr_0.9fr_0.9fr_1fr_minmax(188px,1.25fr)_0.8fr_76px_228px]";
const LIBRARY_LIST_CELL_TRUNCATE = "min-w-0 truncate whitespace-nowrap";
const LIBRARY_LIST_CELL_NOWRAP = "whitespace-nowrap";

function getLibraryListGridClass(showStatusColumn: boolean) {
  return showStatusColumn
    ? LIBRARY_LIST_GRID_WITH_STATUS
    : LIBRARY_LIST_GRID_WITHOUT_STATUS;
}

function getQuestionTypeLabel(
  questionType: unknown,
  labels: {
    single: string;
    sequential: string;
  },
) {
  return questionType === "single_question" ? labels.single : labels.sequential;
}

function buildThumbnailUrl(fileName: unknown) {
  const name = String(fileName ?? "").trim();
  if (!name) {
    return "";
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://thaipilacreate.gforcesolution.com";
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  return `${normalizedBaseUrl}/api/${encodeURIComponent(name)}`;
}

function getTeacherUuidFromLocalStorage() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";

    const parsed = JSON.parse(raw) as { id?: unknown };
    if (!parsed || typeof parsed !== "object") return "";

    if (parsed.id === undefined || parsed.id === null) {
      return "";
    }

    const normalizedId = String(parsed.id).trim();
    return normalizedId;
  } catch {
    return "";
  }
}

function getUserRoleFromLocalStorage() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";

    const parsed = JSON.parse(raw) as { role?: string };
    if (!parsed || typeof parsed !== "object") return "";

    return typeof parsed.role === "string" ? parsed.role.trim() : "";
  } catch {
    return "";
  }
}

function normalizeNotes(notes: unknown): InformationDetailNote[] {
  if (!Array.isArray(notes)) {
    return [];
  }

  const normalized: InformationDetailNote[] = [];

  notes.forEach((item) => {
    const row = item as Record<string, unknown>;
    const id = row.id ?? row.note_id ?? row.id_note;

    if (id === null || id === undefined || String(id).trim() === "") {
      return;
    }

    normalized.push({
      id: String(id),
      name: String(row.name ?? "").trim(),
      detail: String(row.detail ?? "").trim(),
      create_at: String(row.create_at ?? row.createdAt ?? "-").trim() || "-",
    });
  });

  return normalized;
}

function normalizeStatus(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

export default function PilaThailandLibrary({
  mode,
  onOpenRecord,
}: PilaThailandLibraryProps) {
  const isMyLibraryMode = mode === "my-library";
  const isTemplateMode = mode === "template";
  const [rows, setRows] = useState<LibraryRow[]>([]);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>(defaultFilterState);
  const [sortState, setSortState] = useState<LibrarySortState>(
    DEFAULT_LIBRARY_SORT_STATE,
  );
  const [deleteTarget, setDeleteTarget] = useState<LibraryRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusPopup, setStatusPopup] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: "info" | "success" | "error";
  }>({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
  const [editorWizard, setEditorWizard] = useState<GameEditorWizardData | null>(
    null,
  );
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [infoTarget, setInfoTarget] = useState<LibraryRow | null>(null);
  const [noteEditorTarget, setNoteEditorTarget] =
    useState<NoteEditorTarget | null>(null);
  const [gridActionTargetId, setGridActionTargetId] = useState<string | null>(
    null,
  );
  const [removedLessonPopupOpen, setRemovedLessonPopupOpen] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [statusConfirmTarget, setStatusConfirmTarget] =
    useState<LibraryRow | null>(null);
  const [isStatusConfirming, setIsStatusConfirming] = useState(false);
  const [duplicateConfirmTarget, setDuplicateConfirmTarget] =
    useState<LibraryRow | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [libraryReloadVersion, setLibraryReloadVersion] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(20);
  const [copiedRowId, setCopiedRowId] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<{
    open: boolean;
    message: string;
    type: "success" | "error";
    rowId: string | null;
  }>({
    open: false,
    message: "",
    type: "success",
    rowId: null,
  });
  const [copyTarget, setCopyTarget] = useState<CopyTarget | null>(null);
  const [isPreparingCopy, setIsPreparingCopy] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const copiedRowTimerRef = useRef<number | null>(null);
  const copyToastTimerRef = useRef<number | null>(null);
  const isLoadingLibraryRef = useRef(false);
  const lastLibraryLoadAtRef = useRef(0);
  const lastUserStorageSnapshotRef = useRef<string | null>(null);

  const tMain = useTranslations("main");
  const canManageCategoryAndGroupInTemplate =
    currentUserRole === "super_admin" || currentUserRole === "admin";
  const showCategoryGroupButtons = canManageCategoryAndGroupInTemplate
    ? isTemplateMode
    : isMyLibraryMode;
  const isTemplateAdminOrSuperAdmin =
    isTemplateMode &&
    (currentUserRole === "admin" || currentUserRole === "super_admin");

  function getStatusToggleDialogContent(
    currentStatus: boolean,
  ): StatusToggleDialogContent {
    const nextStatus = !currentStatus;

    if (nextStatus) {
      return {
        confirmTitle: tMain("confirmTitlePublish"),
        confirmMessage: tMain("confirmMessagePublish"),
        successTitle: tMain("successTitlePublish"),
        successMessage: tMain("successMessagePublish"),
      };
    }

    return {
      confirmTitle: tMain("confirmTitleUnpublish"),
      confirmMessage: tMain("confirmMessageUnpublish"),
      successTitle: tMain("successTitleUnpublish"),
      successMessage: tMain("successMessageUnpublish"),
    };
  }

  function renderPublishStatusContent(
    isBanned: boolean,
    published: boolean,
    iconSize = 16,
  ) {
    if (isBanned) {
      return (
        <>
          <span className="inline-flex shrink-0 items-center justify-center">
            <FaBan size={iconSize} color="#ffffff" />
          </span>
          <span className="text-[#ffffff]">{tMain("banned")}</span>
        </>
      );
    }

    if (published) {
      return (
        <>
          <span className="inline-flex shrink-0 items-center justify-center">
            <BiWorld size={iconSize} color="#16A34A" />
          </span>
          <span className="text-[#16A34A]">{tMain("publish")}</span>
        </>
      );
    }

    return (
      <>
        <span className="inline-flex shrink-0 items-center justify-center">
          <BiWorld size={iconSize} color="#3b3b3b" />
        </span>
        <span className="text-[#3b3b3b]">{tMain("unpublish")}</span>
      </>
    );
  }

  useEffect(() => {
    return () => {
      if (copiedRowTimerRef.current) {
        window.clearTimeout(copiedRowTimerRef.current);
      }
      if (copyToastTimerRef.current) {
        window.clearTimeout(copyToastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!gridActionTargetId) return;

    const handleOutsideClick = (event: globalThis.MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("[data-grid-action-root='true']")) return;
      closeGridActionModal();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [gridActionTargetId]);

  const prepareCopyUuid = async (row: LibraryRow) => {
    if (row.isBanned) {
      return;
    }

    if (!row.uuid || row.uuid === "-") {
      return;
    }

    setCopyTarget({
      row,
      generatedUuid: "",
    });
    setIsPreparingCopy(true);

    try {
      const teacherUuid = getTeacherUuidFromLocalStorage();
      const normalizedSource = String(row.source ?? "").toLowerCase();
      const generateUuidPayload =
        normalizedSource === "sequence"
          ? {
              uuid_sequenceinfo: row.uuid,
              ...(teacherUuid ? { teacher_uuid: teacherUuid } : {}),
            }
          : {
              uuid_gameinfo: row.uuid,
              ...(teacherUuid ? { teacher_uuid: teacherUuid } : {}),
            };

      const generateUuidResponse =
        await mainAPI.generateUUIDForPIla(generateUuidPayload);
      if (
        !generateUuidResponse ||
        (generateUuidResponse as { status?: string }).status === "failed"
      ) {
        setCopyTarget(null);
        setStatusPopup({
          open: true,
          title: tMain("generateUuidError"),
          message: tMain("generateUuidErrorMessage"),
          type: "error",
        });
        return;
      }

      const generatedUuidResult = ((generateUuidResponse as { data?: unknown })
        .data ?? generateUuidResponse) as {
        uuid_newgen?: unknown;
      };
      const newGeneratedUuid = String(
        generatedUuidResult.uuid_newgen ?? "",
      ).trim();

      if (!newGeneratedUuid) {
        setCopyTarget(null);
        setStatusPopup({
          open: true,
          title: tMain("generateUuidError"),
          message: tMain("uuidNotFoundError"),
          type: "error",
        });
        return;
      }
      setCopyTarget({
        row,
        generatedUuid: newGeneratedUuid,
      });
    } catch (error) {
      console.error("Copy uuid and sync state failed:", error);
      setCopyTarget(null);
      setStatusPopup({
        open: true,
        title: tMain("copyUuidError"),
        message: tMain("copyUuidErrorMessage"),
        type: "error",
      });
    } finally {
      setIsPreparingCopy(false);
    }
  };

  const closeCopyPopup = () => {
    if (isCopying) {
      return;
    }
    setCopyTarget(null);
    setIsPreparingCopy(false);
  };

  const syncCopiedExerciseAgentState = async (
    generatedUuid: string,
    exerciseName: string,
    row: Pick<LibraryRow, "thumbnail" | "uniqueId">,
  ) => {
    try {
      const { default: Agent } = await import("@knowlearning/agents");
      const state = (await Agent.state(generatedUuid)) as any;

      state.name = exerciseName.trim();
      state.reference = {
        player: "thaipilagame.eef.or.th",
        dashboard: "thaipilacreate.gforcesolution.com/live-monitor",
        // dashboard: "thaipilacreate.gforcesolution.com/dashboard-debug",
      };
      state.picture = `https://thaipilacreate.eef.or.th/api/upload/${String(row.thumbnail ?? "").replace(/^\/?(?:upload\/)?/, "")}`;
    } catch (error) {
      console.error("Copy agent state sync failed:", error);
      showCopyToast(row.uniqueId, tMain("copyUuidError"), "error");
    }
  };

  const confirmCopyFromPopup = async (exerciseName: string) => {
    if (!copyTarget?.generatedUuid) {
      return;
    }

    const { row, generatedUuid } = copyTarget;
    const defaultExerciseName = row.exerciseName || row.title || "";
    const resolvedExerciseName = exerciseName.trim() || defaultExerciseName;
    try {
      setIsCopying(true);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(generatedUuid);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = generatedUuid;
        textarea.style.cssText =
          "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    } catch (error) {
      console.error("Clipboard copy failed:", error);
      showCopyToast(row.uniqueId, tMain("copyUuidError"), "error");
      setStatusPopup({
        open: true,
        title: tMain("copyUuidError"),
        message: tMain("copyUuidErrorMessage"),
        type: "error",
      });
      return;
    } finally {
      setIsCopying(false);
    }

    setCopiedRowId(row.uniqueId);
    if (copiedRowTimerRef.current) {
      window.clearTimeout(copiedRowTimerRef.current);
    }
    copiedRowTimerRef.current = window.setTimeout(() => {
      setCopiedRowId(null);
      copiedRowTimerRef.current = null;
    }, 1500);
    showCopyToast(
      row.uniqueId,
      `${tMain("copy")} ${resolvedExerciseName || row.exerciseName || generatedUuid}`,
      "success",
    );

    void syncCopiedExerciseAgentState(generatedUuid, resolvedExerciseName, row);
  };

  const openDuplicateConfirm = (event: MouseEvent, row: LibraryRow) => {
    event.stopPropagation();
    if (row.isBanned || !row.uuid || row.uuid === "-") {
      return;
    }
    if (!row.status) {
      setStatusPopup({
        open: true,
        title: tMain("duplicateUnpublishedError"),
        message: tMain("duplicateUnpublishedErrorMessage"),
        type: "error",
      });
      return;
    }
    setDuplicateConfirmTarget(row);
  };

  const closeDuplicateConfirm = () => {
    if (isDuplicating) {
      return;
    }
    setDuplicateConfirmTarget(null);
  };

  const confirmDuplicateGame = async () => {
    if (!duplicateConfirmTarget) {
      return;
    }

    const sourceUuid = String(duplicateConfirmTarget.uuid ?? "").trim();
    if (!sourceUuid || sourceUuid === "-") {
      setDuplicateConfirmTarget(null);
      setStatusPopup({
        open: true,
        title: tMain("duplicateError"),
        message: tMain("sourceUuidNotFoundError"),
        type: "error",
      });
      return;
    }

    setIsDuplicating(true);
    const teacherUuid = getTeacherUuidFromLocalStorage();
    const response = await mainAPI.cloneGameTemplate({
      source_uuid: sourceUuid,
      ...(teacherUuid ? { teacher_uuid: teacherUuid } : {}),
    });
    setIsDuplicating(false);
    setDuplicateConfirmTarget(null);

    if (!response || (response as { status?: string }).status === "failed") {
      setStatusPopup({
        open: true,
        title: tMain("duplicateError"),
        message: tMain("sourceUuidNotFoundError"),
        type: "error",
      });
      return;
    }

    setStatusPopup({
      open: true,
      title: tMain("duplicateSuccess"),
      message: tMain("systemDuplicateSuccess"),
      type: "success",
    });

    setLibraryReloadVersion((prev) => prev + 1);
  };

  const openDeleteConfirm = (event: MouseEvent, row: LibraryRow) => {
    event.stopPropagation();
    setDeleteTarget(row);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) {
      return;
    }
    setDeleteTarget(null);
  };

  const closeStatusPopup = () => {
    setStatusPopup((prev) => ({ ...prev, open: false }));
  };

  const openPreview = (event: MouseEvent, uuid: string) => {
    event.stopPropagation();
    if (!uuid || uuid === "-") {
      return;
    }

    const previewBaseUrl =
      process.env.NEXT_PUBLIC_BASE_GAME_URL ||
      "https://thaipilagame.gforcesolution.com";
    const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
    const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(uuid)}?preview`;
    setPreviewIframeUrl(previewUrl);
  };

  const closePreviewIframe = () => {
    setPreviewIframeUrl(null);
  };

  const openEditPreview = async (
    event: MouseEvent,
    record: EditRecordTarget,
    extras?: { thumbnailUrl?: string; teacherName?: string },
  ) => {
    event.stopPropagation();
    if (!record.uuid || record.uuid === "-") return;

    const previewBaseUrl =
      process.env.NEXT_PUBLIC_BASE_GAME_URL ||
      "https://thaipilagame.gforcesolution.com";
    const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
    const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(record.uuid)}?preview`;
    const hasGameType = Boolean(String(record.gameType ?? "").trim());
    const fallbackLabel = hasGameType
      ? getLocalizedGameTypeLabel(record.gameType, (key) => tMain(key))
      : getLibraryGameTypeDisplay("", record.source ?? "", (key) => tMain(key));

    const normalizedSource = String(record.source ?? "")
      .toLowerCase()
      .trim();
    const normalizedGameType = String(record.gameType ?? "")
      .toLowerCase()
      .trim();
    const isSequence = normalizedSource === "sequence";
    const isSituation = normalizedGameType === "situation";
    const NO_SWAP_GAME_TYPES = new Set([
      "anagram",
      "whack_a_mole",
      "complete_the_sentence",
    ]);
    const hideGameTypePanel =
      isSequence || isSituation || NO_SWAP_GAME_TYPES.has(normalizedGameType);

    let swapGames: string[] | null = null;
    if (!hideGameTypePanel && hasGameType) {
      try {
        const res = await gameWizardAPI.getSwapOptions(record.gameType);
        const games = (res as { games?: unknown })?.games;
        if (Array.isArray(games)) {
          swapGames = games as string[];
        }
      } catch {
        // fallback: show all options
      }
    }

    setEditorWizard({
      target: record,
      previewUrl,
      thumbnailUrl: extras?.thumbnailUrl ?? "",
      teacherName: extras?.teacherName ?? "",
      gameTypeKey: hasGameType ? record.gameType : "",
      gameTypeLabel: fallbackLabel,
      selectedGameTypeKey: record.gameType,
      hideGameTypePanel,
      swapGames,
    });
  };

  const closeEditorWizard = () => {
    setEditorWizard(null);
  };

  const startEditFromPreview = () => {
    if (!editorWizard) return;
    const { target, selectedGameTypeKey } = editorWizard;
    onOpenRecord?.({ ...target, gameType: selectedGameTypeKey });
    closeEditorWizard();
  };

  const openInformationDetail = (event: MouseEvent, row: LibraryRow) => {
    event.stopPropagation();
    setInfoTarget(row);
  };

  const closeInformationDetail = () => {
    setInfoTarget(null);
  };

  const closeGridActionModal = () => {
    setGridActionTargetId(null);
  };

  const showCopyToast = (
    rowId: string,
    message: string,
    type: "success" | "error",
  ) => {
    setCopyToast({ open: true, message, type, rowId });
    if (copyToastTimerRef.current) {
      window.clearTimeout(copyToastTimerRef.current);
    }
    copyToastTimerRef.current = window.setTimeout(() => {
      setCopyToast((prev) => ({ ...prev, open: false, rowId: null }));
      copyToastTimerRef.current = null;
    }, 2200);
  };

  const openCreateNoteFromDetail = (row: LibraryRow) => {
    setInfoTarget(null);
    setNoteEditorTarget({
      row,
      mode: "create",
    });
  };

  const openUpdateNoteFromDetail = (
    row: LibraryRow,
    note: InformationDetailNote,
  ) => {
    setInfoTarget(null);
    setNoteEditorTarget({
      row,
      mode: "update",
      note,
    });
  };

  const handleDeleteNoteSuccess = (
    detailUniqueId: string,
    noteId: number | string,
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.uniqueId !== detailUniqueId) {
          return row;
        }

        return {
          ...row,
          notes: row.notes.filter((note) => String(note.id) !== String(noteId)),
        };
      }),
    );

    setInfoTarget((prev) => {
      if (!prev || prev.uniqueId !== detailUniqueId) {
        return prev;
      }

      return {
        ...prev,
        notes: prev.notes.filter((note) => String(note.id) !== String(noteId)),
      };
    });
  };

  const handleNoteSaved = (
    note: {
      id: number | string;
      name: string;
      detail: string;
      create_at: string;
    },
    mode: "create" | "update",
  ) => {
    if (!noteEditorTarget) {
      return;
    }

    const rowId = noteEditorTarget.row.uniqueId;

    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.uniqueId !== rowId) {
          return row;
        }

        if (mode === "create") {
          return {
            ...row,
            notes: [...row.notes, note],
          };
        }

        return {
          ...row,
          notes: row.notes.map((item) =>
            String(item.id) === String(note.id)
              ? {
                  ...item,
                  name: note.name,
                  detail: note.detail,
                  create_at: note.create_at || item.create_at,
                }
              : item,
          ),
        };
      }),
    );
  };

  const applyInformationDetailUpdates = (
    row: LibraryRow,
    updates: InformationDetailUpdates,
  ): LibraryRow => {
    const nextGroupId =
      updates.groupId === undefined
        ? row.groupId
        : updates.groupId === null || updates.groupId === ""
          ? null
          : Number.isFinite(Number(updates.groupId))
            ? Number(updates.groupId)
            : row.groupId;

    return {
      ...row,
      ...(updates.thumbnailUrl !== undefined
        ? {
            thumbnailUrl: updates.thumbnailUrl,
            hasThumb: Boolean(String(updates.thumbnailUrl).trim()),
          }
        : {}),
      ...(updates.exerciseName !== undefined
        ? { exerciseName: updates.exerciseName, title: updates.exerciseName }
        : {}),
      ...(updates.description !== undefined
        ? { description: updates.description }
        : {}),
      ...(updates.category !== undefined ? { subject: updates.category } : {}),
      ...(updates.group !== undefined ? { topic: updates.group } : {}),
      ...(updates.groupId !== undefined ? { groupId: nextGroupId } : {}),
    };
  };

  const handleDetailUpdated = (
    detailUniqueId: string,
    updates: InformationDetailUpdates,
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.uniqueId !== detailUniqueId
          ? row
          : applyInformationDetailUpdates(row, updates),
      ),
    );

    setInfoTarget((prev) => {
      if (!prev || prev.uniqueId !== detailUniqueId) {
        return prev;
      }

      return applyInformationDetailUpdates(prev, updates);
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    if (!deleteTarget.gameId) {
      setDeleteTarget(null);
      setStatusPopup({
        open: true,
        title: tMain("deleteError"),
        message: tMain("deleteNotFoundError"),
        type: "error",
      });
      return;
    }

    setIsDeleting(true);
    const source = String(deleteTarget.source ?? "").toLowerCase();
    const response =
      source === "sequence"
        ? await sequenceAPI.softDeleteSequenceById(String(deleteTarget.gameId))
        : await mainAPI.softDeleteGameById(String(deleteTarget.gameId));
    setIsDeleting(false);

    if (!response || (response as { status?: string }).status === "failed") {
      setDeleteTarget(null);
      setStatusPopup({
        open: true,
        title: tMain("deleteError"),
        message: tMain("deleteFailedError"),
        type: "error",
      });
      return;
    }

    setRows((prevRows) =>
      prevRows.filter((row) => row.uniqueId !== deleteTarget.uniqueId),
    );
    setDeleteTarget(null);
    setStatusPopup({
      open: true,
      title: tMain("deleteSuccess"),
      message: tMain("deleteCompleted"),
      type: "success",
    });
  };

  const handleToggleStatus = async (row: LibraryRow, nextStatus: boolean) => {
    if (row.deletedAt || row.isBanned || !row.gameId) {
      return false;
    }

    setStatusUpdatingId(row.uniqueId);
    const source = String(row.source ?? "").toLowerCase();
    const response =
      source === "sequence"
        ? await sequenceAPI.updateSequenceStatus({
            id: row.gameId,
            status: nextStatus,
          })
        : await mainAPI.updateGameStatus({
            id: row.gameId,
            status: nextStatus,
          });
    setStatusUpdatingId(null);

    if (!response || (response as { status?: string }).status === "failed") {
      setStatusPopup({
        open: true,
        title: tMain("updateStatusError"),
        message: tMain("updateStatusFailedError"),
        type: "error",
      });
      return false;
    }

    setRows((prevRows) =>
      prevRows.map((item) =>
        item.uniqueId === row.uniqueId ? { ...item, status: nextStatus } : item,
      ),
    );

    return true;
  };

  const handleStatusButtonClick = (row: LibraryRow) => {
    if (row.isBanned) {
      setRemovedLessonPopupOpen(true);
      return;
    }

    if (row.deletedAt || !row.gameId) {
      return;
    }

    setStatusConfirmTarget(row);
  };

  const closeStatusConfirm = () => {
    if (isStatusConfirming) {
      return;
    }
    setStatusConfirmTarget(null);
  };

  const confirmStatusToggle = async () => {
    if (!statusConfirmTarget) {
      return;
    }

    const target = statusConfirmTarget;
    const nextStatus = !target.status;
    const dialogContent = getStatusToggleDialogContent(target.status);

    setIsStatusConfirming(true);
    const isSuccess = await handleToggleStatus(target, nextStatus);
    setIsStatusConfirming(false);
    setStatusConfirmTarget(null);

    if (!isSuccess) {
      return;
    }

    setStatusPopup({
      open: true,
      title: dialogContent.successTitle,
      message: dialogContent.successMessage,
      type: "success",
    });
  };

  useEffect(() => {
    let mounted = true;

    lastUserStorageSnapshotRef.current = localStorage.getItem("user");

    const loadLessonLibrary = async (force = false) => {
      const now = Date.now();
      const minReloadIntervalMs = 800;

      if (
        !force &&
        (isLoadingLibraryRef.current ||
          now - lastLibraryLoadAtRef.current < minReloadIntervalMs)
      ) {
        return;
      }

      const userRole = getUserRoleFromLocalStorage();
      const isSuperAdmin = userRole === "super_admin";
      const isAdmin = userRole === "admin";
      if (mounted) {
        setIsSuperAdminUser(isSuperAdmin);
        setCurrentUserRole(userRole);
      }
      const teacherUuid = getTeacherUuidFromLocalStorage();

      if (!isSuperAdmin && !isAdmin && !teacherUuid) {
        if (mounted) {
          setRows([]);
          setIsLoading(false);
        }
        return;
      }

      isLoadingLibraryRef.current = true;
      lastLibraryLoadAtRef.current = now;

      if (mounted) {
        setIsLoading(true);
      }

      try {
        const response = isSuperAdmin
          ? await mainAPI.getAllLessonLibraryGameDefault()
          : isAdmin
            ? await mainAPI.getAllLessonLibraryExceptDefaultGames()
            : await mainAPI.getAllLessonLibraryByTeacherUuid(teacherUuid);
        if (!mounted) {
          return;
        }

        if (
          !response ||
          (response as { status?: string }).status === "failed"
        ) {
          setRows([]);
          setIsLoading(false);
          return;
        }

        const sourceRows = Array.isArray(response)
          ? response
          : Array.isArray((response as { data?: unknown[] }).data)
            ? (response as { data: unknown[] }).data
            : [];

        const visibleSourceRows = sourceRows.filter((item) => {
          const row = item as Record<string, unknown>;
          const deleted = row.delete_at ?? row.deleteAt ?? null;
          if (mode === "my-library") {
            return true;
          }
          return (
            deleted === null ||
            deleted === undefined ||
            String(deleted).trim() === ""
          );
        });

        const mappedRows: LibraryRow[] = visibleSourceRows.map(
          (item, index) => {
            const row = item as Record<string, unknown>;
            const rawQuestionType =
              row.question_type == null ? null : String(row.question_type);
            const rawDeletedAt = row.delete_at ?? row.deleteAt;
            const normalizedDeletedAt =
              rawDeletedAt === null ||
              rawDeletedAt === undefined ||
              String(rawDeletedAt).trim() === ""
                ? null
                : String(rawDeletedAt);
            const rawBanAt = row.ban_at ?? row.banAt;
            const normalizedBanAt =
              rawBanAt === null ||
              rawBanAt === undefined ||
              String(rawBanAt).trim() === ""
                ? null
                : String(rawBanAt);

            return {
              uniqueId: String(row.unique_id ?? `row_${index + 1}`),
              gameId: Number.isFinite(Number(row.id)) ? Number(row.id) : null,
              uuid: String(row.uuid ?? "-"),
              title: String(row.exercise_name ?? row.title ?? "-"),
              exerciseName: String(row.exercise_name ?? row.title ?? "-"),
              subject: String(row.subject ?? "-"),
              topic: String(row.topic ?? row.group_name ?? "-"),
              questionType: getQuestionTypeLabel(rawQuestionType, {
                single: tMain("single"),
                sequential: tMain("sequential"),
              }),
              rawQuestionType,
              gameTypeKey: String(row.game_type ?? ""),
              source: String(row.source ?? ""),
              gameType: "",
              createdAt: formatDateShort(
                String(row.create_at ?? row.createdAt ?? "-"),
              ),
              createdAtRaw: String(row.create_at ?? row.createdAt ?? "-"),
              classLevel:
                String(row.class ?? row.class_name ?? "-").trim() || "-",
              updatedAtRaw: String(row.update_at ?? row.updatedAt ?? "-"),
              createdBy:
                String(
                  row.teacher_name ?? row.teacherName ?? row.create_by ?? "-",
                ).trim() || "-",
              description: String(row.description ?? "").trim(),
              notes: normalizeNotes(row.notes),
              thumbnail: String(row.thumbnail ?? ""),
              thumbnailUrl: buildThumbnailUrl(row.thumbnail),
              groupId: Number.isFinite(Number(row.group_id))
                ? Number(row.group_id)
                : null,
              status: normalizeStatus(row.status),
              deletedAt: normalizedDeletedAt,
              banAt: normalizedBanAt,
              isBanned: Boolean(normalizedBanAt),
              hasThumb: Boolean(String(row.thumbnail ?? "").trim()),
              gameDefault: Boolean(row.game_default),
              sequenceDefault: Boolean(row.sequence_default),
            };
          },
        );

        const modeFilteredRows = mappedRows.filter((row) => {
          if (isSuperAdmin) {
            return row.gameDefault === true || row.sequenceDefault === true;
          }

          if (isAdmin) {
            return row.gameDefault === false;
          }

          if (mode === "template") {
            return (
              (row.gameDefault === true || row.sequenceDefault === true) &&
              row.status === true
            );
          }

          // My Exercise: แสดงเฉพาะของครูเอง (ไม่รวม default)
          return row.gameDefault === false && row.sequenceDefault === false;
        });

        setRows(modeFilteredRows);
        setIsLoading(false);
      } finally {
        isLoadingLibraryRef.current = false;
      }
    };

    const shouldReloadFromStorageEvent = () => {
      const nextSnapshot = localStorage.getItem("user");
      if (nextSnapshot === lastUserStorageSnapshotRef.current) {
        return false;
      }
      lastUserStorageSnapshotRef.current = nextSnapshot;
      return true;
    };

    const onUserStorageChanged = () => {
      if (!shouldReloadFromStorageEvent()) {
        return;
      }
      void loadLessonLibrary();
    };

    void loadLessonLibrary(true);

    window.addEventListener("storage", onUserStorageChanged);
    window.addEventListener(USER_STORAGE_CHANGED_EVENT, onUserStorageChanged);

    return () => {
      mounted = false;
      window.removeEventListener("storage", onUserStorageChanged);
      window.removeEventListener(
        USER_STORAGE_CHANGED_EVENT,
        onUserStorageChanged,
      );
    };
  }, [mode, libraryReloadVersion]);

  const availableSubjects = useMemo(() => {
    return Array.from(
      new Set(
        rows
          .map((row) => row.subject.trim())
          .filter((subject) => Boolean(subject) && subject !== "-"),
      ),
    );
  }, [rows]);

  const availableGroupTopics = useMemo(() => {
    return Array.from(
      new Set(
        rows
          .map((row) => row.topic.trim())
          .filter((topic) => Boolean(topic) && topic !== "-"),
      ),
    );
  }, [rows]);

  const localizedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        gameType: getLibraryGameTypeDisplay(
          row.gameTypeKey,
          row.source,
          (key) => tMain(key),
        ),
      })),
    [rows, tMain],
  );

  const filteredRows = useMemo(() => {
    const keyword = filters.search.trim().toLowerCase();

    return localizedRows.filter((row) => {
      const matchesSearch =
        !keyword ||
        row.title.toLowerCase().includes(keyword) ||
        row.subject.toLowerCase().includes(keyword) ||
        row.topic.toLowerCase().includes(keyword);

      const matchesSubject =
        filters.subject === ALL_SUBJECT_FILTER ||
        row.subject === filters.subject;

      const matchesGroupTopic =
        filters.groupTopic === ALL_GROUP_FILTER ||
        row.topic === filters.groupTopic;

      const matchesGameType =
        filters.gameType === ALL_GAME_TYPE_FILTER ||
        !String(filters.gameType ?? "").trim() ||
        row.gameTypeKey === filters.gameType;

      const matchesQuestionMode =
        filters.questionMode === ALL_QUESTION_MODE_FILTER ||
        !String(filters.questionMode ?? "").trim() ||
        (filters.questionMode === QUESTION_MODE_SINGLE_FILTER &&
          row.rawQuestionType === "single_question") ||
        (filters.questionMode === QUESTION_MODE_SERIES_FILTER &&
          row.rawQuestionType === null);

      return (
        matchesSearch &&
        matchesSubject &&
        matchesGroupTopic &&
        matchesGameType &&
        matchesQuestionMode
      );
    });
  }, [localizedRows, filters]);

  const sortedRows = useMemo(
    () => sortLibraryRows(filteredRows, sortState),
    [filteredRows, sortState],
  );

  const totalCount = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.search,
    filters.subject,
    filters.groupTopic,
    filters.gameType,
    filters.questionMode,
    pageSize,
    sortState,
    mode,
    libraryReloadVersion,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (editorWizard) {
    const wizardGameTypeOptions = editorWizard.swapGames
      ? editorWizard.swapGames.map((key) => ({
          key,
          label: getLocalizedGameTypeLabel(key, (messageKey) =>
            tMain(messageKey),
          ),
        }))
      : GAME_TYPE_API_KEYS.map((key) => ({
          key,
          label: getLocalizedGameTypeLabel(key, (messageKey) =>
            tMain(messageKey),
          ),
        }));

    const handleCreateNew = async (gameTypeKey: string) => {
      const teacherUuid = getTeacherUuidFromLocalStorage();
      const res = await gameWizardAPI.cloneTemplateWithGameType({
        source_uuid: editorWizard.target.uuid,
        teacher_uuid: teacherUuid,
        game_type: gameTypeKey,
      });
      const newUuid =
        (res as { uuid?: string; data?: { uuid?: string } })?.uuid ??
        (res as { uuid?: string; data?: { uuid?: string } })?.data?.uuid ??
        editorWizard.target.uuid;
      onOpenRecord?.({
        ...editorWizard.target,
        uuid: newUuid,
        gameType: gameTypeKey,
      });
      closeEditorWizard();
    };

    const handleSwapAndEdit = async (gameTypeKey: string) => {
      await gameWizardAPI.updateGameType({
        game_uuid: editorWizard.target.uuid,
        game_type: gameTypeKey,
      });
      onOpenRecord?.({ ...editorWizard.target, gameType: gameTypeKey });
      closeEditorWizard();
    };

    return (
      <GameEditorWizard
        data={editorWizard}
        gameTypeOptions={wizardGameTypeOptions}
        onSelectGameType={(key) =>
          setEditorWizard((prev) =>
            prev ? { ...prev, selectedGameTypeKey: key } : prev,
          )
        }
        onClose={closeEditorWizard}
        onConfirmEdit={startEditFromPreview}
        onCreateNew={handleCreateNew}
        onSwapAndEdit={handleSwapAndEdit}
      />
    );
  }

  const showStatusColumn = isMyLibraryMode || isTemplateAdminOrSuperAdmin;
  const listGridClass = getLibraryListGridClass(showStatusColumn);

  return (
    <section className="h-full min-w-0 rounded-lg bg-[#ffffff] p-3 sm:p-4 lg:p-6 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]">
      <LibraryHeaderFilters
        title={
          mode === "template" ? tMain("templateLibrary") : tMain("myLibrary")
        }
        availableSubjects={availableSubjects}
        availableGroupTopics={availableGroupTopics}
        showCategoryGroupButtons={showCategoryGroupButtons}
        onFilterChange={setFilters}
        onResetFilters={() => setSortState(DEFAULT_LIBRARY_SORT_STATE)}
      />

      <div className="mt-6 rounded-xl p-1">
        {viewMode === "grid" ? (
          <div className="mb-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className="cursor-pointer rounded p-1 text-sky-600"
              aria-label="Grid view"
              title="Grid view"
            >
              <IoGridOutline className="text-2xl" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className="cursor-pointer rounded p-1 text-neutral-400 hover:text-sky-600"
              aria-label="List view"
              title="List view"
            >
              <LuList className="text-2xl" />
            </button>
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex h-48 items-center justify-center text-lg text-neutral-400">
            {tMain("loadingData")}
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-lg text-neutral-400">
            {tMain("noDataFound")}
          </div>
        ) : viewMode === "list" ? (
          <div className="min-w-0 overflow-x-auto">
            <div className="min-w-[1280px] w-full xl:min-w-0">
              <div className={`grid gap-x-4 gap-y-3 ${listGridClass}`}>
                <LibrarySortableHeader
                  showStatusColumn={showStatusColumn}
                  sortState={sortState}
                  onSortChange={setSortState}
                  labels={{
                    exerciseName: tMain("exerciseName"),
                    status: tMain("status"),
                    subject: tMain("subject"),
                    topic: tMain("topic"),
                    questionType: tMain("questionType"),
                    gameType: tMain("gameType"),
                    createdDate: tMain("createdDate"),
                    preview: tMain("preview"),
                  }}
                  viewControls={
                    <>
                      <button
                        type="button"
                        onClick={() => setViewMode("grid")}
                        className="cursor-pointer rounded p-1 text-neutral-400 hover:text-sky-600"
                        aria-label="Grid view"
                        title="Grid view"
                      >
                        <IoGridOutline className="text-2xl" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewMode("list")}
                        className="cursor-pointer rounded p-1 text-sky-600"
                        aria-label="List view"
                        title="List view"
                      >
                        <LuList className="text-2xl" />
                      </button>
                    </>
                  }
                />

                {paginatedRows.map((row, index) => {
                  const canManageGame =
                    isSuperAdminUser ||
                    (!row.gameDefault && !row.sequenceDefault);
                  const showCenteredCopy =
                    mode === "template" && !canManageGame;
                  const isRowBanned = row.isBanned;
                  const rowNumber = (currentPage - 1) * pageSize + index + 1;
                  const questionTypeLabel = getQuestionTypeLabel(
                    row.rawQuestionType,
                    {
                      single: tMain("single"),
                      sequential: tMain("sequential"),
                    },
                  );
                  return (
                    <div
                      key={row.uniqueId}
                      className="col-span-full grid grid-cols-subgrid items-center gap-x-4 gap-y-3 rounded-x bg-[#F7F7F7] px-6 py-3 text-base text-neutral-600 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                    >
                      <span className="text-sm text-neutral-500">
                        {rowNumber}
                      </span>
                      <span
                        className={`font-medium ${LIBRARY_LIST_CELL_TRUNCATE}`}
                        title={row.title}
                      >
                        {row.title}
                      </span>
                      <span className="flex items-center">
                        {row.hasThumb && row.thumbnailUrl ? (
                          <PhotoProvider>
                            <PhotoView src={row.thumbnailUrl}>
                              <img
                                src={row.thumbnailUrl}
                                alt={row.title}
                                className="h-10 w-10 shrink-0 cursor-pointer rounded object-cover"
                              />
                            </PhotoView>
                          </PhotoProvider>
                        ) : null}
                      </span>
                      {showStatusColumn ? (
                        <button
                          type="button"
                          onClick={() => handleStatusButtonClick(row)}
                          disabled={statusUpdatingId === row.uniqueId}
                          className={`justify-self-center shrink-0 ${getMyLibraryPublishButtonClass(isRowBanned, row.status)}`}
                        >
                          {renderPublishStatusContent(
                            isRowBanned,
                            row.status,
                            16,
                          )}
                        </button>
                      ) : null}
                      <span
                        className={LIBRARY_LIST_CELL_TRUNCATE}
                        title={row.subject}
                      >
                        {row.subject}
                      </span>
                      <span
                        className={LIBRARY_LIST_CELL_TRUNCATE}
                        title={row.topic}
                      >
                        {row.topic}
                      </span>
                      <span
                        className={LIBRARY_LIST_CELL_TRUNCATE}
                        title={questionTypeLabel}
                      >
                        {questionTypeLabel}
                      </span>
                      <span className={LIBRARY_LIST_CELL_NOWRAP}>
                        {row.gameType}
                      </span>
                      <span
                        className={LIBRARY_LIST_CELL_TRUNCATE}
                        title={row.createdAt}
                      >
                        {row.createdAt}
                      </span>
                      <button
                        type="button"
                        onClick={(event) => openPreview(event, row.uuid)}
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center justify-self-center rounded-md border-2 border-[#0092d7] bg-[#DAF2FF] text-[#138FC5] cursor-pointer hover:bg-[#c8ebff]"
                        title="Preview"
                      >
                        <MdOutlineRemoveRedEye className="text-[22px]" />
                      </button>

                      <div className="relative flex shrink-0 items-center justify-end gap-3 whitespace-nowrap pl-2">
                        {!showCenteredCopy &&
                        copyToast.open &&
                        copyToast.rowId === row.uniqueId ? (
                          <div
                            className={`pointer-events-none absolute -top-11 right-0 z-20 w-max max-w-[360px] rounded-md px-2 py-1 text-[11px] leading-4 text-white shadow ${copyToast.type === "success" ? "bg-emerald-600" : "bg-rose-600"}`}
                            style={{
                              animation:
                                "copyBubbleSlideFromRight 2200ms ease-in-out forwards",
                            }}
                          >
                            {copyToast.message}
                          </div>
                        ) : null}
                        {isTemplateAdminOrSuperAdmin ? (
                          <>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setNoteEditorTarget({
                                  row,
                                  mode: "create",
                                });
                              }}
                              className="cursor-pointer text-neutral-700 hover:text-sky-600"
                              title="Note"
                              aria-label="note"
                            >
                              <CgNotes className="text-lg" />
                            </button>
                            {canManageGame ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  if (isRowBanned) {
                                    event.stopPropagation();
                                    return;
                                  }
                                  void openEditPreview(
                                    event,
                                    {
                                      uuid: row.uuid,
                                      gameType: row.gameTypeKey,
                                      source: row.source,
                                      gameId: row.gameId,
                                      title: row.title,
                                    },
                                    {
                                      thumbnailUrl: row.hasThumb
                                        ? row.thumbnailUrl
                                        : "",
                                      teacherName: row.createdBy,
                                    },
                                  );
                                }}
                                disabled={isRowBanned}
                                className={`text-neutral-700 ${isRowBanned ? "cursor-not-allowed opacity-45" : "cursor-pointer hover:text-sky-600"}`}
                                title="Edit"
                                aria-label="edit"
                              >
                                <FiEdit3 className="text-lg" />
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={(event) =>
                                openInformationDetail(event, row)
                              }
                              className="cursor-pointer text-neutral-700 hover:text-sky-600"
                              title="Info"
                            >
                              <IoInformationCircleOutline className="text-xl" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setNoteEditorTarget({
                                  row,
                                  mode: "create",
                                });
                              }}
                              className="cursor-pointer text-neutral-700 hover:text-sky-600"
                              title="Note"
                              aria-label="note"
                            >
                              <CgNotes className="text-lg" />
                            </button>
                            {canManageGame ? (
                              <>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    if (isRowBanned) {
                                      event.stopPropagation();
                                      return;
                                    }
                                    void openEditPreview(
                                      event,
                                      {
                                        uuid: row.uuid,
                                        gameType: row.gameTypeKey,
                                        source: row.source,
                                        gameId: row.gameId,
                                        title: row.title,
                                      },
                                      {
                                        thumbnailUrl: row.hasThumb
                                          ? row.thumbnailUrl
                                          : "",
                                        teacherName: row.createdBy,
                                      },
                                    );
                                  }}
                                  disabled={isRowBanned}
                                  className={`text-neutral-700 ${isRowBanned ? "cursor-not-allowed opacity-45" : "cursor-pointer hover:text-sky-600"}`}
                                  title="Edit"
                                  aria-label="edit"
                                >
                                  <FiEdit3 className="text-lg" />
                                </button>

                                <button
                                  type="button"
                                  onClick={(event) =>
                                    openInformationDetail(event, row)
                                  }
                                  className="cursor-pointer text-neutral-700 hover:text-sky-600"
                                  title="Info"
                                >
                                  <IoInformationCircleOutline className="text-xl" />
                                </button>
                              </>
                            ) : null}
                          </>
                        )}
                        {showCenteredCopy ? (
                          <div className="relative">
                            {copyToast.open &&
                            copyToast.rowId === row.uniqueId ? (
                              <div
                                className={`pointer-events-none absolute -top-11 left-1/2 z-20 w-max max-w-[360px] -translate-x-1/2 rounded-md px-2 py-1 text-[11px] leading-4 text-white shadow ${copyToast.type === "success" ? "bg-emerald-600" : "bg-rose-600"}`}
                                style={{
                                  animation:
                                    "copyBubbleSlideFromRight 2200ms ease-in-out forwards",
                                }}
                              >
                                {copyToast.message}
                              </div>
                            ) : null}
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={(event) =>
                                  openDuplicateConfirm(event, row)
                                }
                                disabled={
                                  isRowBanned || !row.uuid || row.uuid === "-"
                                }
                                className={`text-neutral-700 ${isRowBanned || !row.uuid || row.uuid === "-" ? "cursor-not-allowed opacity-45" : "cursor-pointer hover:text-sky-600"}`}
                                title="Duplicate game"
                              >
                                <MdOutlineControlPointDuplicate className="text-lg" />
                              </button>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (isRowBanned) {
                                    return;
                                  }
                                  void prepareCopyUuid(row);
                                }}
                                disabled={isRowBanned}
                                className={`text-neutral-700 ${isRowBanned ? "cursor-not-allowed opacity-45" : "cursor-pointer hover:text-sky-600"}`}
                                title="Copy uuid"
                              >
                                {copiedRowId === row.uniqueId ? (
                                  <IoCheckmark className="text-xl text-emerald-600" />
                                ) : (
                                  <MdOutlineContentCopy className="text-lg hover:text-sky-600" />
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={(event) =>
                                openDuplicateConfirm(event, row)
                              }
                              disabled={
                                isRowBanned || !row.uuid || row.uuid === "-"
                              }
                              className={`text-neutral-700 ${isRowBanned || !row.uuid || row.uuid === "-" ? "cursor-not-allowed opacity-45" : "cursor-pointer hover:text-sky-600"}`}
                              title="Duplicate game"
                            >
                              <MdOutlineControlPointDuplicate className="text-lg" />
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (isRowBanned) {
                                  return;
                                }
                                void prepareCopyUuid(row);
                              }}
                              disabled={isRowBanned}
                              className={`text-neutral-700 ${isRowBanned ? "cursor-not-allowed opacity-45" : "cursor-pointer hover:text-sky-600"}`}
                              title="Copy uuid"
                            >
                              {copiedRowId === row.uniqueId ? (
                                <IoCheckmark className="text-xl text-emerald-600" />
                              ) : (
                                <MdOutlineContentCopy className="text-lg hover:text-sky-600" />
                              )}
                            </button>
                          </div>
                        )}
                        {!showCenteredCopy && canManageGame ? (
                          <button
                            type="button"
                            onClick={(event) => openDeleteConfirm(event, row)}
                            className="ml-4 cursor-pointer border-l border-gray-300 pl-4 text-red-500 hover:text-red-700"
                            title="Delete"
                            aria-label="delete"
                          >
                            <RiDeleteBinLine className="text-lg" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5 xl:gap-10">
            {paginatedRows.map((row) => {
              const canManageGame =
                isSuperAdminUser || (!row.gameDefault && !row.sequenceDefault);
              return (
                <div key={row.uniqueId} className="relative w-full">
                  <div
                    className="w-full overflow-hidden rounded-lg border border-[#cfcfcf] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                    style={{ aspectRatio: "429 / 239" }}
                  >
                    {row.hasThumb && row.thumbnailUrl ? (
                      <PhotoProvider>
                        <PhotoView src={row.thumbnailUrl}>
                          <img
                            src={row.thumbnailUrl}
                            alt={row.title}
                            className="h-full w-full cursor-pointer object-cover"
                          />
                        </PhotoView>
                      </PhotoProvider>
                    ) : null}
                  </div>
                  <div className="mt-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="line-clamp-2 text-[14px] font-semibold leading-5 text-[#2b2b2b]">
                        {row.title}
                      </p>
                      <div className="flex items-center gap-3">
                        {isMyLibraryMode ? (
                          row.deletedAt ? (
                            <button
                              type="button"
                              onClick={() => setRemovedLessonPopupOpen(true)}
                              className="cursor-pointer text-xs font-semibold text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleStatusButtonClick(row)}
                              disabled={statusUpdatingId === row.uniqueId}
                              className={`${getMyLibraryPublishButtonClass(row.isBanned, row.status)}`}
                            >
                              {renderPublishStatusContent(
                                row.isBanned,
                                row.status,
                                14,
                              )}
                            </button>
                          )
                        ) : null}
                        <div className="relative" data-grid-action-root="true">
                          {copyToast.open &&
                          copyToast.rowId === row.uniqueId ? (
                            <div
                              className={`pointer-events-none absolute -top-11 left-1/2 z-40 w-max max-w-[360px] -translate-x-1/2 rounded-md px-2 py-1 text-[11px] leading-4 text-white shadow ${copyToast.type === "success" ? "bg-emerald-600" : "bg-rose-600"}`}
                              style={{
                                animation:
                                  "copyBubbleSlideFromRight 2200ms ease-in-out forwards",
                              }}
                            >
                              {copyToast.message}
                            </div>
                          ) : null}
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setGridActionTargetId((prev) =>
                                prev === row.uniqueId ? null : row.uniqueId,
                              );
                            }}
                            className="mt-0.5 cursor-pointer rounded-md border border-[#0092d7] p-1 text-[#0092d7] hover:bg-[#e9f8ff]"
                            title="Actions"
                            aria-label="Open actions"
                          >
                            <BsThreeDotsVertical className="text-sm" />
                          </button>

                          {gridActionTargetId === row.uniqueId ? (
                            <div
                              className="absolute right-0 top-full z-30 mt-1 w-56 rounded-xl bg-white p-2 shadow-[0_12px_30px_rgba(0,0,0,0.25)]"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <div className="space-y-1 text-sm">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    openPreview(
                                      event as unknown as MouseEvent,
                                      row.uuid,
                                    );
                                    closeGridActionModal();
                                  }}
                                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-neutral-700 hover:bg-neutral-100"
                                >
                                  <MdOutlineRemoveRedEye className="text-base text-[#138FC5]" />
                                  {tMain("preview")}
                                </button>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    openDuplicateConfirm(
                                      event as unknown as MouseEvent,
                                      row,
                                    );
                                    closeGridActionModal();
                                  }}
                                  disabled={
                                    row.isBanned ||
                                    !row.uuid ||
                                    row.uuid === "-"
                                  }
                                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${row.isBanned || !row.uuid || row.uuid === "-" ? "cursor-not-allowed text-neutral-400" : "text-neutral-700 hover:bg-neutral-100"}`}
                                >
                                  <MdOutlineControlPointDuplicate className="text-base" />
                                  {tMain("duplicateGame")}
                                </button>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (row.isBanned) {
                                      return;
                                    }
                                    void prepareCopyUuid(row);
                                    closeGridActionModal();
                                  }}
                                  disabled={row.isBanned}
                                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${row.isBanned ? "cursor-not-allowed text-neutral-400" : "text-neutral-700 hover:bg-neutral-100"}`}
                                >
                                  <MdOutlineContentCopy className="text-base" />
                                  {tMain("copyUuid")}
                                </button>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setNoteEditorTarget({
                                      row,
                                      mode: "create",
                                    });
                                    closeGridActionModal();
                                  }}
                                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-neutral-700 hover:bg-neutral-100"
                                >
                                  <CgNotes className="text-base" />
                                  {tMain("note")}
                                </button>
                                {canManageGame ? (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      if (row.isBanned) {
                                        event.stopPropagation();
                                        return;
                                      }
                                      void openEditPreview(
                                        event as unknown as MouseEvent,
                                        {
                                          uuid: row.uuid,
                                          gameType: row.gameTypeKey,
                                          source: row.source,
                                          gameId: row.gameId,
                                          title: row.title,
                                        },
                                        {
                                          thumbnailUrl: row.hasThumb
                                            ? row.thumbnailUrl
                                            : "",
                                          teacherName: row.createdBy,
                                        },
                                      );
                                      closeGridActionModal();
                                    }}
                                    disabled={row.isBanned}
                                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${row.isBanned ? "cursor-not-allowed text-neutral-400" : "text-neutral-700 hover:bg-neutral-100"}`}
                                  >
                                    <FiEdit3 className="text-base" />
                                    {tMain("edit")}
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    openInformationDetail(
                                      event as unknown as MouseEvent,
                                      row,
                                    );
                                    closeGridActionModal();
                                  }}
                                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-neutral-700 hover:bg-neutral-100"
                                >
                                  <IoInformationCircleOutline className="text-base" />
                                  {tMain("information")}
                                </button>
                                {canManageGame ? (
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      openDeleteConfirm(
                                        event as unknown as MouseEvent,
                                        row,
                                      );
                                      closeGridActionModal();
                                    }}
                                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-rose-600 hover:bg-rose-50"
                                  >
                                    <RiDeleteBinLine className="text-base" />
                                    {tMain("delete")}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <p className="mt-1 line-clamp-1 text-[12px] text-neutral-500">
                      {tMain("by")} {row.createdBy}
                    </p>
                    <span className="mt-2 inline-block rounded bg-[#FDEF5C] px-2 py-0.5 text-[12px] leading-4 text-[#2b2b2b]">
                      {row.gameType}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!isLoading && filteredRows.length > 0 ? (
        <TablePagination
          totalCount={totalCount}
          totalCountLabel={tMain("totalGames", { count: totalCount })}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      ) : null}

      {deleteTarget ? (
        <ConfirmPopUp
          title={tMain("confirmDelete")}
          message={tMain("confirmMessageDelete")}
          confirmText={tMain("confirm")}
          cancelText={tMain("cancel")}
          onConfirm={() => {
            void handleDelete();
          }}
          onCancel={closeDeleteConfirm}
          isConfirming={isDeleting}
        />
      ) : null}

      {statusConfirmTarget ? (
        <ConfirmPopUp
          title={
            getStatusToggleDialogContent(statusConfirmTarget.status)
              .confirmTitle
          }
          message={
            getStatusToggleDialogContent(statusConfirmTarget.status)
              .confirmMessage
          }
          confirmText={tMain("confirm")}
          cancelText={tMain("cancel")}
          onConfirm={() => {
            void confirmStatusToggle();
          }}
          onCancel={closeStatusConfirm}
          isConfirming={isStatusConfirming}
        />
      ) : null}

      {duplicateConfirmTarget ? (
        <ConfirmPopUp
          title={tMain("confirmDuplicate")}
          message={tMain("confirmMessageDuplicate")}
          confirmText={tMain("confirm")}
          cancelText={tMain("cancel")}
          onConfirm={() => {
            void confirmDuplicateGame();
          }}
          onCancel={closeDuplicateConfirm}
          isConfirming={isDuplicating}
        />
      ) : null}

      {statusPopup.open ? (
        <StatusPopUp
          title={statusPopup.title}
          message={statusPopup.message}
          onClose={closeStatusPopup}
          type={statusPopup.type}
        />
      ) : null}
      <CopyPopUp
        isOpen={Boolean(copyTarget)}
        exerciseName={
          copyTarget?.row.exerciseName || copyTarget?.row.title || ""
        }
        exerciseNameEditable
        detailLine={
          copyTarget
            ? `${copyTarget.row.classLevel} - ${copyTarget.row.subject} - ${formatCompactDate(copyTarget.row.createdAtRaw)}`
            : ""
        }
        copyId={copyTarget?.generatedUuid ?? ""}
        isPreparing={isPreparingCopy}
        isCopying={isCopying}
        onClose={closeCopyPopup}
        onConfirmCopy={(name) => {
          void confirmCopyFromPopup(name);
        }}
      />
      {removedLessonPopupOpen ? (
        <StatusPopUp
          title={tMain("removedLesson")}
          message={tMain("removedLessonMessage")}
          onClose={() => setRemovedLessonPopupOpen(false)}
          type="info"
        />
      ) : null}

      {previewIframeUrl ? (
        <GamePreviewModal
          src={previewIframeUrl}
          iframeTitle="Game Preview"
          onClose={closePreviewIframe}
        />
      ) : null}

      {infoTarget ? (
        <InformationDetail
          isOpen={Boolean(infoTarget)}
          detail={
            infoTarget
              ? ({
                  uniqueId: infoTarget.uniqueId,
                  gameUuid: infoTarget.uuid,
                  gameId: infoTarget.gameId,
                  source: infoTarget.source,
                  subject: infoTarget.subject,
                  category: infoTarget.subject,
                  group: infoTarget.topic,
                  groupId: infoTarget.groupId,
                  exerciseName: infoTarget.exerciseName,
                  thumbnailUrl: infoTarget.thumbnailUrl,
                  createdAt: infoTarget.createdAtRaw,
                  updatedAt: infoTarget.updatedAtRaw,
                  createdBy: infoTarget.createdBy,
                  description: infoTarget.description,
                  notes: infoTarget.notes,
                  banAt: infoTarget.banAt,
                  isBanned: infoTarget.isBanned,
                } satisfies InformationDetailData)
              : null
          }
          onClose={closeInformationDetail}
          onCreateNote={(_detail) => {
            openCreateNoteFromDetail(infoTarget);
          }}
          onEditNote={(_detail, note) => {
            openUpdateNoteFromDetail(infoTarget, note);
          }}
          onDeleteNoteSuccess={handleDeleteNoteSuccess}
          onDetailUpdated={handleDetailUpdated}
        />
      ) : null}

      {noteEditorTarget ? (
        <NotePopUp
          target={{
            gameId: noteEditorTarget.row.gameId,
            source: noteEditorTarget.row.source,
            title: noteEditorTarget.row.title,
          }}
          mode={noteEditorTarget.mode}
          initialNote={noteEditorTarget.note ?? null}
          onSaved={handleNoteSaved}
          onClose={() => setNoteEditorTarget(null)}
        />
      ) : null}

      <style jsx>{`
        @keyframes copyBubbleSlideFromRight {
          0% {
            opacity: 0;
            transform: translateX(calc(-50% + 16px)) translateY(0);
          }
          12% {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          78% {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
